// ChartShell — reusable wrapper for Recharts charts with:
//   • per-chart fullscreen toggle (target = wrapper element, not page)
//   • optional Reset Zoom button (wired by parent via onReset/canReset)
//   • automatic Recharts ResponsiveContainer height swap via FsContext:
//       - normal:     height={420}  (or whatever the caller passes)
//       - fullscreen: height="100%"  (forces percentage-sized SVG)
//
// Why two pieces:
//   FsContext is a runtime channel from ChartShell down to any
//   <FsResponsiveContainer> in its subtree. We could not just CSS-stretch
//   the container — Recharts' ResizeObserver only fires SVG re-layout
//   when the container is percentage-sized (numeric heights freeze the
//   inner <svg> at its initial pixel value). See iter_90 RCA.
//
// CSS rules:
//   :fullscreen / :-webkit-full-screen / :-moz-full-screen MUST be
//   declared as SEPARATE blocks. Combining them with commas makes
//   Chromium/Firefox silently drop the whole rule (historical bug
//   that broke the Liquidity Map fullscreen mode once already).
//
// Extracted from CyclesMapView.jsx in 2026-06-30 to be reused by the
// DCA Simulator and any future Recharts-based view.
import React from "react";
import { ResponsiveContainer } from "recharts";
import { Maximize2, Minimize2, RotateCcw } from "lucide-react";

export const FsContext = React.createContext(false);

export function FsResponsiveContainer({ height, ...rest }) {
  const isFs = React.useContext(FsContext);
  return (
    <ResponsiveContainer
      width="100%"
      height={isFs ? "100%" : height}
      {...rest}
    />
  );
}

export const FULLSCREEN_STYLES = `
  .cycles-chart-shell:fullscreen {
    background: #09090b;
    padding: 24px;
    display: flex;
    flex-direction: column;
  }
  .cycles-chart-shell:-webkit-full-screen {
    background: #09090b;
    padding: 24px;
    display: flex;
    flex-direction: column;
  }
  .cycles-chart-shell:-moz-full-screen {
    background: #09090b;
    padding: 24px;
    display: flex;
    flex-direction: column;
  }
  .cycles-chart-shell:fullscreen > .cycles-chart-shell-body {
    flex: 1 1 auto;
    min-height: 0;
    display: flex;
    flex-direction: column;
  }
  .cycles-chart-shell:-webkit-full-screen > .cycles-chart-shell-body {
    flex: 1 1 auto;
    min-height: 0;
    display: flex;
    flex-direction: column;
  }
  .cycles-chart-shell:-moz-full-screen > .cycles-chart-shell-body {
    flex: 1 1 auto;
    min-height: 0;
    display: flex;
    flex-direction: column;
  }
  /* Force Recharts ResponsiveContainer (which renders a div with an
     inline height: 420px) to stretch to the available flex space in
     fullscreen — otherwise the chart stays 420px tall and leaves the
     bottom half of the screen empty ("medio pantalla" bug). */
  .cycles-chart-shell:fullscreen .recharts-responsive-container {
    flex: 1 1 auto;
    height: auto !important;
    min-height: 0;
  }
  .cycles-chart-shell:-webkit-full-screen .recharts-responsive-container {
    flex: 1 1 auto;
    height: auto !important;
    min-height: 0;
  }
  .cycles-chart-shell:-moz-full-screen .recharts-responsive-container {
    flex: 1 1 auto;
    height: auto !important;
    min-height: 0;
  }
`;

export function ChartShell({
  title,
  subtitle,
  onReset,
  canReset,
  children,
  testId,
}) {
  const ref = React.useRef(null);
  const [isFs, setIsFs] = React.useState(false);
  React.useEffect(() => {
    function fire() {
      const fsEl =
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.mozFullScreenElement;
      setIsFs(fsEl === ref.current);
      setTimeout(() => window.dispatchEvent(new Event("resize")), 50);
      setTimeout(() => window.dispatchEvent(new Event("resize")), 250);
    }
    document.addEventListener("fullscreenchange", fire);
    document.addEventListener("webkitfullscreenchange", fire);
    document.addEventListener("mozfullscreenchange", fire);
    return () => {
      document.removeEventListener("fullscreenchange", fire);
      document.removeEventListener("webkitfullscreenchange", fire);
      document.removeEventListener("mozfullscreenchange", fire);
    };
  }, []);
  function toggle() {
    const el = ref.current;
    if (!el) return;
    if (isFs) {
      (
        document.exitFullscreen ||
        document.webkitExitFullscreen ||
        document.mozCancelFullScreen
      )?.call(document);
    } else {
      const req =
        el.requestFullscreen ||
        el.webkitRequestFullscreen ||
        el.mozRequestFullScreen;
      if (req) req.call(el);
    }
  }
  return (
    <div
      ref={ref}
      data-testid={testId}
      className="cycles-chart-shell mb-6 rounded-xl border border-zinc-800 bg-zinc-950/60 p-4"
    >
      <div className="mb-2 flex items-start justify-between gap-2">
        <div className="min-w-0">
          {title && (
            <div className="text-sm font-semibold text-zinc-300">{title}</div>
          )}
          {subtitle && (
            <p className="mt-0.5 text-xs text-zinc-500">{subtitle}</p>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-1">
          {onReset && (
            <button
              type="button"
              onClick={onReset}
              disabled={!canReset}
              data-testid={`${testId}-reset-zoom`}
              className="inline-flex items-center gap-1 rounded-md border border-zinc-700 bg-zinc-900 px-2 py-1 text-[11px] text-zinc-300 transition hover:border-zinc-500 hover:text-white disabled:cursor-not-allowed disabled:opacity-40"
              aria-label="Reset zoom"
              title="Reset zoom"
            >
              <RotateCcw className="h-3 w-3" />
              <span className="hidden sm:inline">Reset</span>
            </button>
          )}
          <button
            type="button"
            onClick={toggle}
            data-testid={`${testId}-fullscreen-toggle`}
            className="inline-flex items-center gap-1 rounded-md border border-zinc-700 bg-zinc-900 px-2 py-1 text-[11px] text-zinc-300 transition hover:border-zinc-500 hover:text-white"
            aria-label={isFs ? "Exit fullscreen" : "Enter fullscreen"}
            title={isFs ? "Exit fullscreen" : "Enter fullscreen"}
          >
            {isFs ? (
              <Minimize2 className="h-3 w-3" />
            ) : (
              <Maximize2 className="h-3 w-3" />
            )}
          </button>
        </div>
      </div>
      <div className="cycles-chart-shell-body">
        <FsContext.Provider value={isFs}>{children}</FsContext.Provider>
      </div>
    </div>
  );
}
